export const NBN_SERVICE_QUALIFICATION_PATH = "/service-qualification-nbn";

const COPPER_ACCESS_TECHNOLOGIES = [
  "Fibre To The Curb",
  "Fibre To The Node",
  "Fibre To The Building"
];

export function nbnSqDisplayMode(path) {
  if (path === NBN_SERVICE_QUALIFICATION_PATH) return "both";
  return "regular";
}

export function sqRequestIncludesEe(path, isNbnEeAdmin) {
  return path === NBN_SERVICE_QUALIFICATION_PATH && !!isNbnEeAdmin;
}

export function nbnSqFailureMessage(payload) {
  if (!payload) return "Site qualification failed";
  const result =
    payload.sqError || payload.sq || payload.sqee ? payload : payload.data;
  if (result && result.sqError && result.sqError.message) {
    return result.sqError.message;
  }
  if (payload.message) return payload.message;
  return "Site qualification failed";
}

function yesNo(value) {
  return value == true ? "Yes" : "No";
}

function buildNfas(siteRestriction) {
  const nfas = [];
  const locationFeatures = siteRestriction.supportingRelatedLocationFeatures;
  if (locationFeatures) {
    if (locationFeatures.networkBoundaryPoint) {
      nfas.push({
        name: "Network Boundary Point",
        value: locationFeatures.networkBoundaryPoint
      });
    }
    if (locationFeatures.newDevelopmentsChargeApplies !== null) {
      nfas.push({
        name: "New Developments Charge Applies",
        value: yesNo(locationFeatures.newDevelopmentsChargeApplies == true)
      });
    }
  }

  const productFeatures = siteRestriction.supportingProductFeatures;
  const feature = productFeatures && productFeatures[0];
  if (productFeatures && feature) {
    if (feature.multicast != null) {
      nfas.push({
        name: "Multicast",
        value: yesNo(feature.multicast == true)
      });
    }
    nfas.push({
      name: "TR-069 UNI-V",
      value: yesNo(feature.tR069UNIV == true || feature.TR069UNIV == true)
    });
    nfas.push({
      name: "FTP UNI-V",
      value: yesNo(feature.ftpuniv == true || feature.FTPUNIV == true)
    });

    if (Array.isArray(feature.capacityAvailability)) {
      const supportingType = feature.type;
      feature.capacityAvailability.forEach(x => {
        if (x.highSpeedNotLessThan !== undefined && x.highSpeedNotLessThan) {
          nfas.push({
            name:
              supportingType +
              " " +
              x.featureType +
              " High Speed Tiers (greater or equal to " +
              x.capacity +
              x.unitOfMeasure +
              ")",
            value: yesNo(x.available == true)
          });
        } else {
          nfas.push({
            name:
              supportingType +
              " " +
              x.featureType +
              " " +
              x.capacity +
              x.unitOfMeasure +
              " Capacity",
            value: yesNo(x.available == true)
          });
        }
      });
    }

    if (Array.isArray(feature.speedTierAvailability)) {
      nfas.push({
        name: "TC4 Speed Tier Availability",
        value: feature.speedTierAvailability.join(", "),
        rawValue: feature.speedTierAvailability
      });
    }
  }

  return nfas;
}

function enrichSupportingResources(resources) {
  let hasNtd = false;
  resources.forEach(x => {
    if (x.type === "NTD") {
      hasNtd = true;
      const ntdDetails = [];
      if (x.NTDLocation) {
        ntdDetails.push({ name: "Location", value: x.NTDLocation });
      }
      if (x.NTDType) {
        ntdDetails.push({ name: "Type", value: x.NTDType });
      }
      if (x.NTDBatteryBackup) {
        ntdDetails.push({
          name: "BatteryPowerUnit",
          value: yesNo(x.NTDBatteryBackup.batteryPowerUnit == true)
        });
      }
      if (x.NTDBandwidth && x.NTDBandwidth.length > 0) {
        const bandwidthDetails = [];
        x.NTDBandwidth.forEach(y => {
          if (y.bandwidthType == "RemainingBandwidthUpstream") {
            bandwidthDetails.push({
              name: "Remaining Capacity Upstream",
              value: `${y.bandwidth} ${y.unitOfMeasure}`,
              rawValue: y.bandwidth,
              order: 2
            });
          } else if (y.bandwidthType == "RemainingBandwidthDownstream") {
            bandwidthDetails.push({
              name: "Remaining Capacity Downstream",
              value: `${y.bandwidth} ${y.unitOfMeasure}`,
              rawValue: y.bandwidth,
              order: 1
            });
          }
        });
        bandwidthDetails.sort((a, b) => a.order - b.order);
        bandwidthDetails.forEach(detail => {
          ntdDetails.push({
            name: detail.name,
            value: detail.value,
            rawValue: detail.rawValue
          });
        });
      }
      if (x.speedTiersSupported && x.speedTiersSupported.length > 0) {
        const supportedSpeedTiers = x.speedTiersSupported.filter(
          y => y.supported
        );
        ntdDetails.push({
          name: "TC4 Speed Tiers Supported",
          value: supportedSpeedTiers.map(y => y.featureType).join(", "),
          rawValue: supportedSpeedTiers
        });
      }
      x.ntdDetails = ntdDetails;
    }

    if (x.type === "CopperLineResource") {
      const cpiDetails = [];
      if (x.copperBandwidthRates) {
        x.copperBandwidthRates.forEach(y => {
          cpiDetails.push({
            name: y.featureType + " " + y.bandwidthType,
            value: y.bandwidth
          });
        });
      }
      if (x.NBNServiceStatus) {
        cpiDetails.push({
          name: "NBNServiceStatus",
          value: x.NBNServiceStatus
        });
      } else {
        x.NBNServiceStatus = "Line Not In Use";
        cpiDetails.push({
          name: "NBNServiceStatus",
          value: "Line Not In Use"
        });
      }
      if (x.copperPairStatus) {
        cpiDetails.push({
          name: "CopperPairStatus",
          value: x.copperPairStatus
        });
      }
      if (x.subsequentInstallationChargeApplies != null) {
        cpiDetails.push({
          name: "SubsequentInstallationChargeApplies",
          value: yesNo(x.subsequentInstallationChargeApplies == true)
        });
      }
      if (x.serviceabilityClass) {
        cpiDetails.push({
          name: "ServiceabilityClass",
          value: x.serviceabilityClass
        });
      }
      x.cpiDetails = cpiDetails;
    }
  });
  return hasNtd;
}

function addDetail(details, name, value) {
  if (value === undefined || value === null || value === "") return;
  details.push({ name: name, value: value });
}

function addRow(rows, name, value) {
  rows.push({
    name: name,
    value: value === undefined || value === null ? "" : String(value)
  });
}

function readField(source, name) {
  if (!source) return undefined;
  const value = source[name];
  if (value !== undefined && value !== null && value !== "") return value;
  return undefined;
}

function asList(value) {
  if (!value) return [];
  return Array.isArray(value) ? value : [value];
}

function moneyText(amount) {
  if (amount === undefined || amount === null || amount === "") return "";
  if (typeof amount === "string" || typeof amount === "number") return String(amount);
  const unit = readField(amount, "unit");
  const value = readField(amount, "value");
  return [unit, value].filter(part => part !== undefined && part !== null && part !== "").join(" ");
}

function priceDisplayName(name, itemType) {
  if (
    itemType === "FBCQuoteItem" ||
    name === "FBC" ||
    (name && /fibre build contribution/i.test(name))
  ) {
    return "Build cost price";
  }
  return name || "";
}

export function eeQuoteDetails(eeQuote) {
  if (!eeQuote) return [];
  const details = [];
  addDetail(details, "Quote Id", readField(eeQuote, "id"));
  addDetail(details, "Quote Status", readField(eeQuote, "status"));
  addDetail(details, "Quote Date", readField(eeQuote, "quoteDate"));
  addDetail(details, "Quote Description", readField(eeQuote, "description"));
  addDetail(details, "Quote Zone", readField(eeQuote, "zone"));
  addDetail(details, "Quote SLR", readField(eeQuote, "slr"));
  addDetail(details, "Quote Fibre Build Category", readField(eeQuote, "fibreBuildCategory"));
  addDetail(details, "Estimated Delivery Time", readField(eeQuote, "estimatedDeliveryTime"));
  addDetail(details, "Quote POI Name", readField(eeQuote, "poiName"));

  return details;
}

export function eeQuotePrices(eeQuote) {
  if (!eeQuote) return [];
  const prices = [];
  asList(eeQuote.quoteItem).forEach(item => {
    const itemType = item["@type"] || item.type || "";
    asList(item.quotePrice).forEach(price => {
      const amount = moneyText(price && price.price);
      if (!amount) return;
      prices.push({
        amount: amount,
        priceType: (price && price.priceType) || "",
        name: priceDisplayName(price && price.name, itemType),
        description: (price && price.description) || ""
      });
    });
  });
  return prices;
}

function priceRowLabel(price) {
  const type = price.priceType ? ` (${price.priceType})` : "";
  return `${price.name || "Price"}${type}`;
}

export function formatNbnEeSqDisplay(siteQualification, address, eeQuote) {
  if (!siteQualification || !siteQualification.siteRestriction) return null;
  const siteRestriction = siteQualification.siteRestriction;
  const technology = siteRestriction.supportingTechnology || {};
  const boundaries = siteRestriction.supportingRelatedSiteBoundaries || {};
  const locationFeatures = siteRestriction.supportingRelatedLocationFeatures || {};
  const status = siteRestriction.status || "";
  const businessFibre =
    technology.businessFibre == null ? "" : yesNo(technology.businessFibre == true);
  const newDevelopmentCharge =
    locationFeatures.newDevelopmentsChargeApplies == null
      ? ""
      : yesNo(locationFeatures.newDevelopmentsChargeApplies == true);
  const qualification = [];
  addRow(qualification, "Status", status);
  addRow(qualification, "Business Fibre", businessFibre);
  addRow(qualification, "Fibre Build Category", locationFeatures.fibreBuildCategory);
  addRow(qualification, "Reason", technology.serviceabilityClassReason);
  addRow(qualification, "Service Class", technology.serviceabilityClass);
  addRow(qualification, "Primary Access Technology", technology.primaryAccessTechnology);
  addRow(qualification, "UNI Zone", boundaries.zone);
  addRow(qualification, "POI Name", boundaries.localPOI);
  addRow(qualification, "Region", boundaries.region);
  addRow(qualification, "CSA Id", boundaries.CSAId);
  addRow(qualification, "Estimated Availability Date", technology.estimatedAvailabilityDate);
  addRow(qualification, "New Developments Charge Applies", newDevelopmentCharge);

  const prices = eeQuotePrices(eeQuote);
  const serviceable = status === "Valid";
  const quote = [];
  if (serviceable) {
    eeQuoteDetails(eeQuote).forEach(detail => quote.push(detail));
    prices.forEach(price => addRow(quote, priceRowLabel(price), price.amount));
    if (!quote.length) {
      addRow(quote, "Quote", "Not available yet, run the SQ again");
    }
  }

  const sections = [{ title: "Service Qualification", rows: qualification }];
  if (quote.length) sections.push({ title: "Quote", rows: quote });

  return {
    sq: siteQualification,
    formatted: {
      status: status,
      serviceability: status,
      serviceabilityClass: technology.serviceabilityClass || "",
      accessTechnology: technology.primaryAccessTechnology || "",
      zone: boundaries.zone || "",
      fibreBuildCategory: locationFeatures.fibreBuildCategory || "",
      region: boundaries.region || "",
      poiName: boundaries.localPOI || "",
      csaid: boundaries.CSAId || "",
      estimatedAvailabilityDate: technology.estimatedAvailabilityDate || "",
      latitude: address && address.latitude != null ? address.latitude : "",
      longitude: address && address.longitude != null ? address.longitude : "",
      sections: sections,
      details: qualification.filter(row => row.value !== "").concat(quote.filter(row => row.value !== "")),
      prices: prices
    },
    serviceable: serviceable
  };
}

function parameterValue(parameters, name) {
  if (!Array.isArray(parameters)) return "";
  const match = parameters.find(item => item && item.name === name);
  return match && match.value != null ? match.value : "";
}

export function legacyNbnSqFields(result) {
  const parameters = result && result.parameters && result.parameters.parameter;
  const additional =
    result && result.address && result.address.additionalParameters;
  let latitude = parameterValue(additional, "latitude");
  let longitude = parameterValue(additional, "longitude");
  if (!latitude && result && result.address && result.address.latitude != null) {
    latitude = result.address.latitude;
  }
  if (!longitude && result && result.address && result.address.longitude != null) {
    longitude = result.address.longitude;
  }
  return {
    serviceabilityClass: parameterValue(parameters, "ServiceabilityClass"),
    accessTechnology: parameterValue(parameters, "PrimaryAccessTechnology"),
    csaid: parameterValue(parameters, "CSAID"),
    latitude: latitude,
    longitude: longitude,
    region: (result && (result.Region || result.region)) || ""
  };
}

export function formatLegacyNbnSqDisplay(result) {
  if (!result || (result.sq && result.sq.siteRestriction)) return null;
  const parameters = result.parameters && result.parameters.parameter;
  if (!Array.isArray(parameters) || parameters.length === 0) return null;
  const fields = legacyNbnSqFields(result);
  return {
    legacy: true,
    sq: null,
    formatted: {
      serviceabilityClass: fields.serviceabilityClass,
      serviceability: result.serviceability || "",
      csaid: fields.csaid,
      region: fields.region,
      accessTechnology: fields.accessTechnology,
      latitude: fields.latitude,
      longitude: fields.longitude,
      serviceabilityDate: ""
    },
    nfas: parameters,
    isPending: false,
    hasNtd: false,
    isCopperAccess: false,
    serviceable: true
  };
}

function qualificationErrorMessage(error) {
  return error && error.message ? error.message : "";
}

function emptySqPanel(key, title, message) {
  return {
    key: key,
    title: title,
    empty: true,
    emptyText: "No result",
    emptyMessage: message || ""
  };
}

export function buildSqResultPanels(data, options) {
  const source = data || {};
  const showEe = !!(options && options.showEe);
  const showEmpty = !!(options && options.showEmpty);
  const panels = [];
  if (showEe) {
    const ee = formatNbnEeSqDisplay(source.sqee, source.address, source.eeQuote);
    if (ee) panels.push(Object.assign({ key: "ee", title: "*NBN EE SQ" }, ee));
    else if (showEmpty) {
      panels.push(emptySqPanel("ee", "*NBN EE SQ", qualificationErrorMessage(source.sqeeError)));
    }
  }
  const regular =
    formatNbnSqDisplay(source.sq, source.address) ||
    formatLegacyNbnSqDisplay(source);
  if (regular) panels.push(Object.assign({ key: "regular", title: "*NBN SQ" }, regular));
  else if (showEmpty) {
    panels.push(emptySqPanel("regular", "*NBN SQ", qualificationErrorMessage(source.sqError)));
  }
  return panels;
}

export function formatNbnSqDisplay(siteQualification, address) {
  if (!siteQualification || !siteQualification.siteRestriction) return null;
  const displayQualification = JSON.parse(JSON.stringify(siteQualification));
  const siteRestriction = displayQualification.siteRestriction;

  if (!Array.isArray(siteRestriction.supportingResource)) {
    siteRestriction.supportingResource = siteRestriction.supportingResource
      ? [siteRestriction.supportingResource]
      : [];
  }

  const nfas = buildNfas(siteRestriction);
  const hasNtd = enrichSupportingResources(siteRestriction.supportingResource);
  const technology = siteRestriction.supportingTechnology || {};
  const boundaries = siteRestriction.supportingRelatedSiteBoundaries || {};
  const reason = technology.serviceabilityClassReason || "";
  const isPending = String(reason)
    .toLowerCase()
    .includes("pending");
  let serviceabilityDate = "";
  if (isPending) {
    serviceabilityDate =
      technology.serviceabilityDate && technology.serviceabilityDate !== ""
        ? technology.serviceabilityDate
        : "Not Available";
  }

  return {
    sq: displayQualification,
    formatted: {
      serviceabilityClass: technology.serviceabilityClass || "",
      serviceability: siteRestriction.serviceabilityStatus,
      csaid: boundaries.CSAId || "",
      region: boundaries.region || "",
      accessTechnology: technology.primaryAccessTechnology || "",
      latitude: address && address.latitude != null ? address.latitude : "",
      longitude: address && address.longitude != null ? address.longitude : "",
      serviceabilityDate: serviceabilityDate
    },
    nfas: nfas,
    isPending: isPending,
    hasNtd: hasNtd,
    isCopperAccess:
      COPPER_ACCESS_TECHNOLOGIES.indexOf(technology.primaryAccessTechnology) !==
      -1,
    serviceable: siteRestriction.serviceabilityStatus !== "Rejected"
  };
}
